import { yup } from '@framework/validation';
import dayjs from 'dayjs';
import { RichiestaE002 } from '../schema/schemas';

export enum PDNDVerificationType {
  FiscalCode = 'fiscalCode',
  Registry = 'registry',
  Id = 'id',
}
export enum PDNDVerificationLocationType {
  Italian = 'italian',
  Foreign = 'foreign',
  Exceptional = 'exceptional',
}

type BornType = Exclude<
  RichiestaE002['criteriRicerca']['datiNascita'],
  undefined
>['luogoNascita'];

export type IPDNDVerificationRequestRegistrySearch = Omit<
  RichiestaE002['criteriRicerca'] & {
    datiNascita: RichiestaE002['criteriRicerca']['datiNascita'] & {
      luogoNascita?: BornType & {
        locationType?: PDNDVerificationLocationType;
      };
    };
  },
  'idANPR' | 'codiceFiscale'
>;

export interface IPDNDVerificationRequest {
  search: {
    type: PDNDVerificationType;
    idANPR?: RichiestaE002['criteriRicerca']['idANPR'] | null;
    codiceFiscale?: RichiestaE002['criteriRicerca']['codiceFiscale'] | null;
    registry?: IPDNDVerificationRequestRegistrySearch;
  };
  referenceDate: string;
  reason: string;
}

export interface IPDNDWrappedVerificationRequest
  extends IPDNDVerificationRequest {
  operationId: string;
  user: IPDNDUserData;
}

export interface IPDNDUserData {
  levelOfAccess: string;
  id: string;
  location?: string | null;
}

export const pdndVerificationRequestValidator: yup.ObjectSchema<IPDNDVerificationRequest> =
  yup
    .object({
      search: yup
        .object({
          type: yup
            .string()
            .required()
            .default(PDNDVerificationType.FiscalCode)
            .oneOfEnum(PDNDVerificationType),

          /** Identificativo Unico Nazionale */
          idANPR: yup
            .string()
            .notRequired()
            .label('ID ANPR')
            .when(['type'], ([type], schema) =>
              type === PDNDVerificationType.Id
                ? schema.required()
                : schema.strip(true),
            ),

          /** Ricerca per CF */
          codiceFiscale: yup
            .string()
            .notRequired()
            .trim()
            .fiscalNumber()
            .label('Codice Fiscale')
            .when(['type'], ([type], schema) =>
              type === PDNDVerificationType.FiscalCode
                ? schema.required()
                : schema.strip(true),
            ),

          /** Ricerca per dati anagrafici */
          registry: yup
            .object({
              cognome: yup
                .string()
                .trim()
                .when(['senzaCognome'], ([senzaCognome], schema) =>
                  senzaCognome ? schema.notRequired() : schema.required(),
                )
                .label('Cognome'),
              nome: yup
                .string()
                .trim()
                .when(['senzaNome'], ([senzaNome], schema) =>
                  senzaNome ? schema.notRequired() : schema.required(),
                )
                .label('Nome'),
              senzaNome: yup
                .mixed()
                .notRequired()
                .when(['$isSubmit'], ([isSubmit], schema) =>
                  isSubmit
                    ? schema.transform(value =>
                        value === true ? '1' : undefined,
                      )
                    : schema,
                )
                .label('Senza Nome'),
              senzaCognome: yup
                .mixed()
                .notRequired()
                .when(['$isSubmit'], ([isSubmit], schema) =>
                  isSubmit
                    ? schema.transform(value =>
                        value === true ? '1' : undefined,
                      )
                    : schema,
                )
                .label('Senza Cognome'),
              sesso: yup.string().required().label('Sesso'),
              datiNascita: yup
                .object({
                  dataEvento: yup
                    .string()
                    .stringDate()
                    // .italianFormat()
                    .required()
                    .label('Data di Nascita'),
                  senzaGiorno: yup
                    .mixed()
                    .notRequired()
                    .when(['$isSubmit'], ([isSubmit], schema) =>
                      isSubmit
                        ? schema.transform(value =>
                            value === true ? '1' : undefined,
                          )
                        : schema,
                    )
                    .label('Senza Giorno'),
                  senzaGiornoMese: yup
                    .mixed()
                    .notRequired()
                    .when(['$isSubmit'], ([isSubmit], schema) =>
                      isSubmit
                        ? schema.transform(value =>
                            value === true ? '1' : undefined,
                          )
                        : schema,
                    )
                    .label('Senza Giorno e Mese'),
                  luogoNascita: yup
                    .object({
                      locationType: yup
                        .string()
                        .oneOfEnum(PDNDVerificationLocationType)
                        .notRequired()
                        .when(['luogoEccezionale'], ([ecceptional], schema) =>
                          ecceptional ? schema.notRequired() : schema.required(),
                        )
                        .label('Tipo Luogo di Nascita'),
                      // .default(PDNDVerificationLocationType.Italian),
                      comune: yup
                        .object({
                          nomeComune: yup
                            .string()
                            .required()
                            .trim()
                            .label('Nome Comune'),
                          siglaProvinciaIstat: yup
                            .string()
                            .required()
                            .trim()
                            .label('Sigla Provincia'),
                        })
                        .notRequired()
                        .when(['locationType'], ([locationType], schema) =>
                          locationType === PDNDVerificationLocationType.Italian
                            ? schema.required()
                            : schema.strip(true),
                        )
                        .label('Comune'),
                      localita: yup
                        .object({
                          descrizioneStato: yup
                            .string()
                            .required()
                            .trim()
                            .label('Stato di Nascita'),
                          codiceStato: yup
                            .string()
                            .required()
                            .trim()
                            .label('Codice Stato'),
                        })
                        .when(
                          ['locationType', 'luogoEccezionale'],
                          ([locationType, ecceptional], schema) =>
                            ecceptional
                              ? schema.enabled(false)
                              : locationType ===
                                PDNDVerificationLocationType.Foreign
                              ? schema.required()
                              : schema.strip(true),
                        )
                        .notRequired()
                        .label('Località'),

                      // Non richiesto per la ricerca.
                      luogoEccezionale: yup
                        .mixed()
                        .notRequired()
                        .when(['$isSubmit'], ([isSubmit], schema) =>
                          isSubmit
                            ? schema.transform(value =>
                                value === true ? 'S' : undefined,
                              )
                            : schema,
                        )
                        .label('Luogo Eccezionale'),
                    })
                    .notRequired(),
                })
                .notRequired(),
            })
            .when(['type'], ([type], schema) =>
              type === PDNDVerificationType.Registry
                ? schema.required()
                : schema.strip(true),
            ) as yup.ObjectSchema<IPDNDVerificationRequestRegistrySearch>,
        })
        .required(),
      referenceDate: yup
        .string()
        .stringDate()
        .required()
        .default(() => dayjs().format('YYYY-MM-DD')),
      reason: yup.string().required().trim().label("Motivo dell'accertamento"),
    })
    .required();

export const pdndWrappedVerificationRequestValidator: yup.ObjectSchema<IPDNDWrappedVerificationRequest> =
  pdndVerificationRequestValidator.shape({
    operationId: yup.string().required().trim().label('ID Operazione'),
    user: yup
      .object({
        levelOfAccess: yup
          .string()
          .required()
          .trim()
          .label('Livello di Accesso'),
        id: yup.string().required().trim().label('ID Utente'),
        location: yup.string().notRequired().trim().label('Sede'),
      })
      .required(),
  });
